import React, { useContext, useEffect, useLayoutEffect, useState } from 'react'
import { Route, Routes, useNavigate } from 'react-router-dom'

import LoadingContainer from 'App/components/LoadingContainer/LoadingContainer'
import WrapperLayout from 'App/components/WrapperLayout/WrapperLayout'
import {
  AccessCode,
  AccountApproval,
  Communications,
  CoordinatorData,
  DependentsData,
  PersonalData,
  Privacy,
  SchoolData,
  SchoolDataRoleSelection,
  TeacherClasses,
  TermsOfUse,
} from 'App/views'
import { DASHBOARD, MANAGEABLE_SCHOOLS } from 'graphql/queries'
import UserContext from 'stores/UserStore'

import {
  clearShouldRedirectFromCookies,
  getShouldRedirectFromCookies,
  graphqlClient,
  handleGraphQLErrorsWithToast,
} from 'utils'
import { ROUTES } from 'utils/constants'

import WrapperRoutes from './components/WrapperRoutes'
import LinkedResponsible from './views/LinkedResponsible'

const App = () => {
  const [firstAccess, setFirstAccess] = useState(true)
  const [loadingDashboard, setLoadingDashboard] = useState(true)
  const [dashboardData, setDashboardData] = useState(null)
  const [loadingManageableSchool, setLoadingManageableSchool] = useState(true)
  const [manageableSchool, setManageableSchool] = useState(null)
  const userStore = useContext(UserContext)
  const navigate = useNavigate()

  const loading = loadingManageableSchool || loadingDashboard

  const getDashboard = async () => {
    try {
      const { data } = await graphqlClient().query({
        query: DASHBOARD,
      })

      setDashboardData(data)
    } catch (e) {
      handleGraphQLErrorsWithToast(
        e,
        'Não foi possível carregar os dados da dashboard. Tente novamente mais tarde.',
      )
    } finally {
      setLoadingDashboard(false)
    }
  }

  const getManageableSchools = async () => {
    try {
      const { data } = await graphqlClient().query({
        query: MANAGEABLE_SCHOOLS,
      })

      setManageableSchool(data)
    } catch (e) {
      handleGraphQLErrorsWithToast(
        e,
        'Não foi possível carregar os dados das escolas. Tente novamente mais tarde.',
      )
    } finally {
      setLoadingManageableSchool(false)
    }
  }

  useEffect(() => {
    getDashboard()
    getManageableSchools()
  }, [])

  useLayoutEffect(() => {
    if (dashboardData && manageableSchool && firstAccess) {
      const loadData = async () => {
        if (!userStore.person) {
          userStore.setPerson(dashboardData.person)
        }
        if (!userStore.manageableSchool) {
          userStore.setManageableSchool(manageableSchool.person?.schools)
        }
        setFirstAccess(false)
      }
      loadData()
    }

    if (getShouldRedirectFromCookies()) {
      clearShouldRedirectFromCookies()
      navigate(ROUTES.PERFIL_ALUNOS_VINCULADOS)
    }
  }, [dashboardData, manageableSchool, firstAccess, userStore, navigate])

  if (loading || (dashboardData && firstAccess)) {
    return <LoadingContainer text='Carregando informações..' fullscreen />
  }

  return (
    <WrapperLayout>
      <Routes>
        <Route path='/' element={<WrapperRoutes component={PersonalData} data={dashboardData} />} />
        <Route
          path={ROUTES.PERFIL_CODIGO_ACESSO}
          element={<WrapperRoutes component={AccessCode} data={dashboardData} />}
        />
        <Route
          path={ROUTES.PERFIL_DADOS_PESSOAIS}
          element={<WrapperRoutes component={PersonalData} data={dashboardData} />}
        />
        <Route
          path={ROUTES.PERFIL_RESPONSAVEIS_VINCULADOS}
          element={<WrapperRoutes component={LinkedResponsible} data={dashboardData} />}
        />
        <Route
          path={ROUTES.PERFIL_DADOS_ESCOLARES}
          element={<WrapperRoutes component={SchoolData} data={dashboardData} schoolAccess />}
        />
        <Route
          path={ROUTES.PERFIL_ALUNOS_VINCULADOS}
          element={<WrapperRoutes component={DependentsData} data={dashboardData} schoolAccess />}
        />
        <Route
          path={ROUTES.PERFIL_DADOS_ESCOLARES_FUNCOES}
          element={
            <WrapperRoutes component={SchoolDataRoleSelection} data={dashboardData} schoolAccess />
          }
        />
        <Route path={ROUTES.PERFIL_DADOS_ESCOLARES_FUNCOES_PROFESSOR_SEGMENTO}>
          <Route
            path=':levelId'
            element={<WrapperRoutes component={TeacherClasses} data={dashboardData} schoolAccess />}
          />
        </Route>
        <Route
          path={ROUTES.PERFIL_DADOS_ESCOLARES_FUNCOES_COORDENADOR}
          element={<WrapperRoutes component={CoordinatorData} data={dashboardData} schoolAccess />}
        />
        <Route
          path={ROUTES.PERFIL_APROVACAO_CONTA}
          element={<WrapperRoutes component={AccountApproval} data={dashboardData} />}
        />
        <Route
          path={ROUTES.PERFIL_PRIVACIDADE}
          element={<WrapperRoutes component={Privacy} data={dashboardData} />}
        />
        <Route
          path={ROUTES.PERFIL_COMUNICACOES}
          element={<WrapperRoutes component={Communications} data={dashboardData} />}
        />

        <Route
          path={ROUTES.PERFIL_TERMOS_DE_USO}
          element={<WrapperRoutes component={TermsOfUse} data={dashboardData} />}
        />
      </Routes>
    </WrapperLayout>
  )
}

export default App
